/**
 * Zod schema for bond trade capture (DEAL ENTRY ONLY).
 *
 * Reflects backend DTO at io.trms.api.dto.DealCaptureRequestDto.
 *
 * Architecture note (verified against backend, 2026-05):
 *   The ticket books against an existing instrument via `instrument_id`, but
 *   the backend still validates bond deal `details` against
 *   schemas/deals/spot/fixed_income/vanilla.json during capture. That means the
 *   frontend must echo the selected instrument's core bond economics
 *   (coupon_rate, face_value, issue_date, maturity_date) plus settlement_date
 *   in the deal payload. FRN conventions still live on the instrument-definition
 *   surface (InstrumentManager) and are not entered directly on this trade
 *   ticket.
 *
 * This ticket selects an EXISTING instrument and captures trade economics +
 * booking details only.
 */
import { z } from 'zod';
import {
  BigDecimalString,
  BuySell,
  CounterpartyRef,
  CurrencyCode,
  IsoDateString,
  OptionalIsoDateString,
  PartyRef,
  PortfolioRef,
} from './shared';

// ---------------------------------------------------------------------------
// ISIN — 12-char alphanumeric, starts with 2 uppercase letters (country code).
// ---------------------------------------------------------------------------
export const Isin = z
  .string()
  .regex(/^[A-Z]{2}[A-Z0-9]{10}$/, 'Must be a valid 12-character ISIN');

export type Isin = z.infer<typeof Isin>;

// ---------------------------------------------------------------------------
// Bond trade capture schema — deal entry only.
// ---------------------------------------------------------------------------

/**
 * Object-only schema (no cross-field refinements).
 * Used for partial payload validation at the registry boundary, where
 * .partial() would conflict with superRefine.
 */
export const BondTradeBaseSchema = z.object({
  /**
   * ISIN of the selected bond instrument. Used to display/identify the
   * resolved instrument; the resolved `instrumentId` is what is sent on the
   * wire.
   */
  isin: Isin,

  /**
   * Resolved instrument id. Required at submit — the ticket blocks booking
   * until the typed identifier resolves to an existing instrument.
   */
  instrumentId: z.string().min(1).optional(),

  /** Trade direction from the perspective of the booking entity. */
  side: BuySell,

  /** Face/notional amount as a BigDecimal string. */
  notional: BigDecimalString,

  /** Notional currency (ISO 4217). Derived from the selected instrument. */
  currency: CurrencyCode,

  /**
   * Bond maturity date — display-only, sourced from the selected instrument.
   * Not entered on the ticket; kept so the success receipt can show it.
   */
  maturityDate: OptionalIsoDateString,

  /** Trade settlement date. */
  settlementDate: OptionalIsoDateString,

  /** Counterparty to the trade. */
  counterparty: CounterpartyRef,

  /** Booking portfolio. */
  portfolio: PortfolioRef,

  /** Optional internal legal entity used for SSI and treasury routing. */
  internalParty: PartyRef.optional(),

  /** Clean price as a percentage of par (BigDecimal string, e.g. "99.875"). */
  cleanPrice: BigDecimalString.optional(),

  /** Accrued interest as a BigDecimal string. Calculated by backend if omitted. */
  accruedInterest: BigDecimalString.optional(),

  /** Free-text trade comment. */
  comment: z.string().max(500).optional(),
});

export type BondTradeBaseSchema = z.infer<typeof BondTradeBaseSchema>;

/**
 * Derived schema with cross-field refinements. Booking requires a resolved
 * instrument id (the picker must have matched an existing instrument).
 */
export const BondTradeSchema = BondTradeBaseSchema.superRefine((val, ctx) => {
  if (val.instrumentId === undefined || val.instrumentId.trim() === '') {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['instrumentId'],
      message: 'instrumentNotResolved',
    });
  }
});

export type BondTradeSchema = z.infer<typeof BondTradeSchema>;

// ---------------------------------------------------------------------------
// Response shape returned by POST /api/v1/deals (bond variant).
// ---------------------------------------------------------------------------
export const BondDealResponse = z.object({
  deal_id: z.string(),
  isin: Isin,
  side: BuySell,
  notional: BigDecimalString,
  currency: CurrencyCode,
  coupon_rate: BigDecimalString,
  maturity_date: IsoDateString,
  settlement_date: IsoDateString.optional(),
  clean_price: BigDecimalString.optional(),
  accrued_interest: BigDecimalString.optional(),
  status: z.enum(['PENDING', 'CONFIRMED', 'REJECTED', 'CANCELLED']),
  created_at: z.string(),
});

export type BondDealResponse = z.infer<typeof BondDealResponse>;
